import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import {
  buttonSecondary,
  SectionHeading,
  tone,
  useOpenAgentDemo,
} from '@/components/agents/primitives';

import LightMarkdown from './LightMarkdown';

export interface FaqItem {
  question: string;
  answer: string;
}

// Clinicians ask about HIPAA before anything else.
const CLINICAL_FAQ: FaqItem = {
  question: 'Is this tool HIPAA-compliant? Do you sign a BAA?',
  answer:
    'Yes. Easy-Peasy.AI supports HIPAA-compliant use for clinical documentation and offers a Business Associate Agreement (BAA) for clinical accounts. You can review, sign, and download the BAA in your account settings: [Manage your BAA](/settings/baa).',
};

/** Markdown links and emphasis as plain text, for the FAQPage schema. */
const plainText = (markdown: string) =>
  markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The template's questions (English only, like the corpus they come from),
 * with FAQPage structured data.
 */
export default function TemplateFaq({
  faqs: baseFaqs,
  isClinical = false,
}: {
  faqs: FaqItem[];
  isClinical?: boolean;
}) {
  const openChat = useOpenAgentDemo();
  const faqs = isClinical ? [CLINICAL_FAQ, ...baseFaqs] : baseFaqs;
  if (!faqs.length) return null;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: plainText(answer) },
    })),
  };

  return (
    <section id='faq' className='pt-24 sm:pt-32'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title='Questions, answered.'
            lead='Something else on your mind? Ask our assistant, it replies in seconds.'
          />
          <button
            type='button'
            onClick={openChat}
            className={clsxm(buttonSecondary, 'mt-8')}
          >
            <span className='relative flex h-2 w-2'>
              <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
            </span>
            Ask a question
          </button>
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }, index) => (
              <li key={question}>
                <details className='group' open={index === 0}>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    {question}
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <div className='max-w-2xl pb-7 pe-12'>
                    <LightMarkdown text={answer} size='lg' />
                  </div>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
